
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, FileText, Calendar, Shield, Users, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const InsuranceClaimProcessPage = () => {
  const majorCarriers = [
    { name: "State Farm", phone: "1-800-STATE-FARM", website: "statefarm.com" },
    { name: "Allstate", phone: "1-800-ALLSTATE", website: "allstate.com" },
    { name: "USAA", phone: "1-800-531-USAA", website: "usaa.com" },
    { name: "Farmers Insurance", phone: "1-800-FARMERS", website: "farmers.com" },
    { name: "NC Farm Bureau", phone: "1-800-662-3276", website: "ncfb.com" },
    { name: "Nationwide", phone: "1-800-421-3535", website: "nationwide.com" },
    { name: "Progressive", phone: "1-800-PROGRESSIVE", website: "progressive.com" },
    { name: "Liberty Mutual", phone: "1-800-290-8711", website: "libertymutual.com" }
  ];

  return (
    <Layout>
      <Helmet>
        <title>The Insurance Claim Process | Carolina Trophy Roofs</title>
        <meta name="description" content="Understanding the insurance claim process for roof damage in North Carolina. Learn how Carolina Trophy Roofs helps as your Contractor of Record with documentation, estimates, and construction planning." />
        <meta name="keywords" content="insurance claim process, roof damage claim, contractor of record, North Carolina insurance, roof insurance claim" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/insurance-claim-process" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">The Insurance Claim Process</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Shield className="mr-2 h-4 w-4" />
              Insurance Guidance
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              The Insurance Claim Process
            </h1>
            <p className="text-xl text-gray-600">
              Understanding your role and ours in the insurance claim process
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-2">Our Role as Your Contractor of Record</h2>
              <p className="text-blue-700">
                Carolina Trophy Roofs serves as your <strong>Contractor of Record</strong>, providing expert 
                documentation, detailed estimates, and comprehensive construction planning. We work within 
                our licensing to help you navigate the claim process while you maintain direct communication 
                with your insurance carrier.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-6">Step-by-Step Claim Process</h2>
            
            <div className="space-y-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">1</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Report Your Claim</h3>
                    <p className="text-gray-600 mb-3">
                      Contact your insurance carrier immediately after discovering damage. We can help you 
                      find your carrier's claim hotline and determine an approximate date of loss.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">What You'll Need:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Policy number</li>
                        <li>• Date of loss (storm date)</li>
                        <li>• Description of damage</li>
                        <li>• Contact information</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">2</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Professional Damage Assessment</h3>
                    <p className="text-gray-600 mb-3">
                      Our certified professionals conduct a comprehensive inspection, documenting all 
                      visible and hidden damage using advanced techniques and equipment.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Documentation Process:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Detailed photo and video documentation</li>
                        <li>• Measurements and technical assessments</li>
                        <li>• Code compliance evaluation</li>
                        <li>• Hidden damage investigation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">3</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Insurance Adjuster Meeting</h3>
                    <p className="text-gray-600 mb-3">
                      We meet with the insurance adjuster on your property to present our findings 
                      and ensure all damage is properly documented and understood.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">During the Adjuster Meeting:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Present comprehensive damage documentation</li>
                        <li>• Point out areas of concern</li>
                        <li>• Discuss construction methodology</li>
                        <li>• Address adjuster questions professionally</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">4</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Detailed Construction Estimate</h3>
                    <p className="text-gray-600 mb-3">
                      We provide a comprehensive estimate based on proper construction practices, 
                      local building codes, and materials needed to restore your property to pre-damage conditions.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Estimate Includes:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Line-item breakdown of materials and labor</li>
                        <li>• Code upgrade requirements</li>
                        <li>• Proper construction sequencing</li>
                        <li>• Manufacturer specifications</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1">5</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Claim Resolution & Construction</h3>
                    <p className="text-gray-600 mb-3">
                      Once your claim is approved, we execute the construction plan with the same 
                      attention to detail used in our documentation process.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Construction Phase:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Permit acquisition and scheduling</li>
                        <li>• Quality materials and workmanship</li>
                        <li>• Regular progress updates</li>
                        <li>• Final inspection and warranty</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Insurance Carrier Claim Hotlines</h2>
            <p className="mb-4">
              Need to file a claim? Here are the direct hotlines for major carriers serving North Carolina:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {majorCarriers.map((carrier, index) => (
                <div key={index} className="bg-white p-4 rounded-lg border">
                  <h4 className="font-semibold text-lg mb-2">{carrier.name}</h4>
                  <div className="space-y-1">
                    <div className="flex items-center text-sm">
                      <Phone className="h-4 w-4 mr-2 text-primary" />
                      <a href={`tel:${carrier.phone}`} className="text-primary hover:underline">
                        {carrier.phone}
                      </a>
                    </div>
                    <div className="text-sm text-gray-600">
                      {carrier.website}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">When Claims Require Additional Support</h3>
              <p className="text-yellow-700 mb-3">
                If your insurance carrier doesn't act in good faith or if complex disputes arise, 
                we can introduce you to licensed public adjusters in our professional network 
                who can take your claim to appraisal when necessary.
              </p>
              <div className="flex items-center text-sm text-yellow-700">
                <Users className="h-4 w-4 mr-2" />
                <span>Professional public adjuster referrals available</span>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Important Considerations</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    What We Do
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• Document all storm damage thoroughly</li>
                    <li>• Prepare detailed construction estimates</li>
                    <li>• Meet with insurance adjusters</li>
                    <li>• Provide expert construction planning</li>
                    <li>• Execute approved construction work</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <Shield className="h-5 w-5 text-blue-500 mr-2" />
                    Claim Filing & Negotiation
                  </h4>
                  <ul className="text-sm space-y-1">
                    <li>• You file the claim directly with your carrier</li>
                    <li>• You negotiate claim terms with your carrier</li>
                    <li>• Licensed public adjusters handle appraisal process</li>
                    <li>• We provide technical support and documentation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Need Help with Your Insurance Claim?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Get expert documentation and construction planning from your trusted Contractor of Record.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Start Your Documentation</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InsuranceClaimProcessPage;
